import { useRef, useCallback, useEffect } from 'react';
/**
 * Hook for handling multi-touch queue item gestures on a specific element
 * Supports: double tap, long press, 2-finger tap, 3-finger tap, spread apart, slide left
 */
export function useQueueGestures(elementRef, handlers) {
    const lastTapTime = useRef(0);
    const lastTapPosition = useRef({ x: 0, y: 0 });
    const longPressTimer = useRef(null);
    const touchStartTime = useRef(0);
    const touchStartPositions = useRef([]);
    const initialDistance = useRef(0);
    const hasMoved = useRef(false);
    // Detect double tap
    const handleDoubleTap = useCallback((touch) => {
        if (hasMoved.current)
            return;
        const now = Date.now();
        const x = touch.clientX;
        const y = touch.clientY;
        // Check if this is a double tap (second tap within 300ms, within 50px)
        if (now - lastTapTime.current < 300) {
            const distance = Math.sqrt(Math.pow(x - lastTapPosition.current.x, 2) +
                Math.pow(y - lastTapPosition.current.y, 2));
            if (distance < 50) {
                handlers.onDoubleTap?.();
                lastTapTime.current = 0;
                return;
            }
        }
        lastTapTime.current = now;
        lastTapPosition.current = { x, y };
    }, [handlers]);
    // Detect long press (touch and hold)
    const handleLongPressStart = useCallback(() => {
        if (longPressTimer.current)
            clearTimeout(longPressTimer.current);
        longPressTimer.current = setTimeout(() => {
            handlers.onLongPress?.();
        }, 500);
    }, [handlers]);
    // Handle touch start
    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 0)
            return;
        hasMoved.current = false;
        touchStartTime.current = Date.now();
        // Store initial touch positions for gesture detection
        touchStartPositions.current = Array.from(e.touches).map(t => ({
            x: t.clientX,
            y: t.clientY
        }));
        // Calculate initial distance for 2-touch gestures
        if (e.touches.length >= 2) {
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            initialDistance.current = Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) +
                Math.pow(touch2.clientY - touch1.clientY, 2));
        }
        // Detect double tap and long press for single touch
        if (e.touches.length === 1) {
            handleDoubleTap(e.touches[0]);
            handleLongPressStart();
        }
    }, [handleDoubleTap, handleLongPressStart]);
    // Handle touch move for spread apart and slide left
    const handleTouchMove = useCallback((e) => {
        hasMoved.current = true;
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
        }
        const touches = Array.from(e.touches);
        // Spread apart detection (two fingers spreading vertically)
        if (touches.length === 2 && touchStartPositions.current.length === 2) {
            const touch1 = touches[0];
            const touch2 = touches[1];
            const currentDistance = Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) +
                Math.pow(touch2.clientY - touch1.clientY, 2));
            const distanceDelta = currentDistance - initialDistance.current;
            // If spreading apart (distance increasing by >40px)
            if (Math.abs(distanceDelta) > 40) {
                handlers.onSpreadApart?.(distanceDelta);
            }
        }
        // Slide left detection (single touch moving left)
        if (touches.length === 1 &&
            touchStartPositions.current.length === 1) {
            const startPos = touchStartPositions.current[0];
            const currentTouch = touches[0];
            const slideDistance = startPos.x - currentTouch.clientX;
            // If sliding left more than 30px
            if (slideDistance > 30 && slideDistance < 200) {
                handlers.onSlideLeft?.(slideDistance);
            }
        }
    }, [handlers]);
    // Handle touch end
    const handleTouchEnd = useCallback((e) => {
        // Clear long press timer
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
        const touches = Array.from(e.touches);
        const touchElapsed = Date.now() - touchStartTime.current;
        // 2-finger tap (two fingers tapped and released quickly, without much movement)
        if (e.changedTouches.length === 2 &&
            touches.length === 0 &&
            touchElapsed < 300 &&
            !hasMoved.current) {
            handlers.onTwoFingerTap?.();
        }
        // 3-finger tap (three fingers tapped and released quickly)
        if (e.changedTouches.length === 3 &&
            touches.length === 0 &&
            touchElapsed < 300 &&
            !hasMoved.current) {
            handlers.onThreeFingerTap?.();
        }
        touchStartPositions.current = [];
        initialDistance.current = 0;
        hasMoved.current = false;
    }, [handlers]);
    // Attach event listeners to the element
    useEffect(() => {
        const element = elementRef.current;
        if (!element)
            return;
        const options = { passive: false };
        element.addEventListener('touchstart', handleTouchStart, options);
        element.addEventListener('touchmove', handleTouchMove, options);
        element.addEventListener('touchend', handleTouchEnd, options);
        return () => {
            element.removeEventListener('touchstart', handleTouchStart);
            element.removeEventListener('touchmove', handleTouchMove);
            element.removeEventListener('touchend', handleTouchEnd);
            if (longPressTimer.current) {
                clearTimeout(longPressTimer.current);
            }
        };
    }, [elementRef, handleTouchStart, handleTouchMove, handleTouchEnd]);
}
/**
 * Hook for detecting device shake
 */
export function useShakeDetection(onShake, threshold = 15) {
    useEffect(() => {
        let lastX = 0, lastY = 0, lastZ = 0;
        let shakeCount = 0;
        let lastShakeTime = 0;
        const handleDeviceMotion = (event) => {
            const acceleration = event.accelerationIncludingGravity;
            if (!acceleration)
                return;
            const x = acceleration.x || 0;
            const y = acceleration.y || 0;
            const z = acceleration.z || 0;
            // Calculate change in acceleration
            const deltaX = Math.abs(x - lastX);
            const deltaY = Math.abs(y - lastY);
            const deltaZ = Math.abs(z - lastZ);
            const totalDelta = deltaX + deltaY + deltaZ;
            // If significant motion detected
            if (totalDelta > threshold) {
                const now = Date.now();
                // Count shakes within 500ms window
                if (now - lastShakeTime < 500) {
                    shakeCount++;
                    // If we've detected 3+ shakes, trigger the handler
                    if (shakeCount >= 3) {
                        onShake();
                        shakeCount = 0;
                        lastShakeTime = 0;
                    }
                }
                else {
                    shakeCount = 1;
                    lastShakeTime = now;
                }
            }
            lastX = x;
            lastY = y;
            lastZ = z;
        };
        // Request permission for DeviceMotion (iOS 13+)
        if (typeof DeviceMotionEvent !== 'undefined' &&
            // @ts-ignore
            typeof DeviceMotionEvent.requestPermission === 'function') {
            // @ts-ignore
            DeviceMotionEvent.requestPermission()
                .then((permissionState) => {
                if (permissionState === 'granted') {
                    window.addEventListener('devicemotion', handleDeviceMotion);
                }
            })
                .catch(console.error);
        }
        else {
            // Non-iOS 13+ devices
            window.addEventListener('devicemotion', handleDeviceMotion);
        }
        return () => {
            window.removeEventListener('devicemotion', handleDeviceMotion);
        };
    }, [onShake, threshold]);
}
